import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TopNav,
  IconButton,
  Icon,
  icons,
  Avatar,
  EmptyState,
  Button,
  theme,
} from '@agentisai/design-system';
import {
  MAX_ATTACHMENT_BYTES,
  MAX_TEXT_LEN,
  type Attachment,
  type TurnArtifact,
  type WorkstreamMode,
} from '@agentisai/shared-types';
import { useAuth } from '../auth/AuthProvider';
import { CreateWorkstreamPanel } from '../components/CreateWorkstreamPanel';
import { FilesRailBody, NewFileAction } from '../components/FilesRailBody';
import { FileViewPanel } from '../components/FileViewPanel';
import { UserMenu } from '../components/UserMenu';
import { WorkspaceShell } from '../components/WorkspaceShell';
import {
  agentLabel,
  agentVariant,
  WorkspaceConversation,
} from '../components/WorkspaceConversation';
import { artifactIdOf } from '../lib/artifactKey';
import { deriveOpenFileIds } from '../lib/openFiles';
import { useViewportTier } from '../hooks/useViewportTier';
import { useMinVisible } from '../hooks/useMinVisible';
import {
  FILE_PARAM,
  FILES_PARAM,
  NewWorkstreamAction,
  WorkstreamsRailBody,
  WS_PARAM,
} from '../components/WorkstreamsRailBody';
import { useWorkstreams } from '../queries/useWorkstreams';
import { useMessages } from '../queries/useMessages';
import { useArtifacts } from '../queries/useArtifacts';
import { useUploadArtifact } from '../queries/useUploadArtifact';
import { validateUpload } from '../lib/validateUpload';
import { sendChatTurn, createWorkstream, runWorkstreamChatTurn } from '../queries/client';
import { keys } from '../queries/keys';
import { describeError } from '../lib/describeError';
import { formatMessageTime } from '../lib/formatMessageTime';

/** Parse the `?files=` open-tab list (comma-separated opaque ids) into an ordered,
 * de-duplicated array, dropping empties (a hand-crafted `?files=A,A` must not yield
 * two tabs with the same key). */
function parseFileTabs(raw: string | null): string[] {
  return raw ? [...new Set(raw.split(',').filter(Boolean))] : [];
}

/**
 * A single turn in the client-held transcript. `role` + `content` are the
 * API wire shape (posted as `messages[]` on every chat turn and on create);
 * `createdAt` is the server-authoritative RFC-3339 instant, stored RAW and
 * formatted at render (frontend-architecture R2) — never a prebaked string.
 */
type TurnEntry = {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  /**
   * Which agent(s) produced an assistant turn (ADR-0054). Absent on user turns,
   * on every message persisted before `Message.agent` existed, and on a reply
   * from a server that predates the stamp — such a turn renders under the
   * generic "Workstream Agent", never a guessed name. More than one on a staged
   * multi-agent turn, whose reply is a composite of their answers (the same
   * "Engineer → QA" reading as the status row).
   */
  agents?: string[];
};

/**
 * Read a PDF into the base64 `Attachment` wire shape. Resolves `null` on a read
 * failure rather than rejecting: the file is uploaded to the ledger either way,
 * so a failed inline copy costs readability this turn, not the file.
 */
function readInlineAttachment(file: File): Promise<Attachment | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      // data URL → bare base64 (the wire format AttachmentSchema validates).
      const result = typeof reader.result === 'string' ? reader.result : '';
      const data = result.split(',')[1] ?? '';
      resolve(data ? { name: file.name, mediaType: 'application/pdf', data } : null);
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/**
 * The one staged file that can ride inline on a turn: a PDF small enough for the
 * synchronous invoke payload. `read_artifact` returns text and cannot extract
 * PDF content, so this is an agent's only way to read one — which is why BOTH
 * send paths (pre- and post-create) pick it the same way.
 */
function inlinePdf(files: readonly File[]): File | undefined {
  return files.find((f) => {
    const validated = validateUpload(f);
    return (
      validated.ok && validated.contentType === 'application/pdf' && f.size <= MAX_ATTACHMENT_BYTES
    );
  });
}

function errorMessage(err: unknown): string {
  // Friendly, specific copy per error class (validation / server / network),
  // not a blanket "Something went wrong" — see lib/describeError.
  return describeError(err).userMessage;
}

/**
 * WorkstreamSession — ONE chat's worth of state (transcript, pending id,
 * ledger, mode…). HomePage remounts it via `key` when the active chat
 * changes, so every per-chat state slot resets at once — no manual reset
 * list to drift out of date as slots are added.
 *
 * S-F1: renders the first-login EMPTY STATE for a fresh tenant, replicating
 * the `Genesis / Sample Workspaces / StratBridge_Daniel` DS story in SHAPE,
 * brand-swapped to the signed-in tenant (BG Private / Jaco for Drop 1).
 *
 * S14 wired the "+ Create First Workstream" CTA to open the modal.
 * S15 built CreateWorkstreamPanel (ChatPanel fluid + seeded intro + Composer).
 * S16 wires the two useMutations — chat turn + explicit create — so the panel
 * is a live conversational round-trip against the S05 canned-reply handlers.
 */
function WorkstreamSession({
  initialWorkstreamId,
  autoOpenCreate,
  onNew,
  onCreated,
}: {
  /** Rail-selected workstream to open into (null = fresh draft chat). */
  readonly initialWorkstreamId: string | null;
  /** Open the create modal on mount ("+ New" remounts into an open modal). */
  readonly autoOpenCreate: boolean;
  /** Start a fresh draft chat (HomePage remounts this component). */
  readonly onNew: () => void;
  /** Reports the created workstreamId up so HomePage can sync the URL. */
  readonly onCreated: (id: string) => void;
}) {
  const { user, tenantBrand, getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(autoOpenCreate);

  // Drives the main-pane empty-state copy + CTA. Only the loaded-and-empty
  // case shows "Create First Workstream"; once any workstream exists the
  // main pane prompts the user to pick one from the rail instead (opening
  // a workstream into a real workspace is deferred to a later drop).
  const workstreamsQuery = useWorkstreams();
  const hasWorkstreams = (workstreamsQuery.data?.data.length ?? 0) > 0;

  // Client-held transcript — the chat-companion model. Posted as `messages[]`
  // on every chat turn and as the final body on create. Server is stateless.
  const [transcript, setTranscript] = useState<TurnEntry[]>([]);
  const [composerValue, setComposerValue] = useState('');
  const [workstreamId, setWorkstreamId] = useState<string | null>(initialWorkstreamId);

  // Transcript hydration (Drop 7 / S05, ADR-0047): reopening an existing
  // workstream mounts this session fresh (keyed by id) with an empty transcript,
  // so seed it from the persisted messages. The fetch goes through a query hook
  // (R4, not effect-for-data). Seeding is the React-sanctioned "adjust state
  // when data arrives" pattern — setState guarded during render, NOT in an
  // effect (react-hooks/set-state-in-effect). It runs once: after the seed the
  // client-held buffer is the live truth the chat mutations append to (so this
  // is not stored-derived-state, R2). A pre-create draft has no id → the query
  // is disabled and nothing seeds.
  const messagesQuery = useMessages(initialWorkstreamId);
  const [hydrated, setHydrated] = useState(initialWorkstreamId === null);
  if (!hydrated && messagesQuery.data) {
    setTranscript(
      messagesQuery.data.data.messages.map((m) => ({
        role: m.role,
        content: m.content,
        // Persisted messages carry their own instant; fall back to now for any
        // legacy row that predates timestamped persistence.
        createdAt: m.createdAt ?? new Date().toISOString(),
        // Likewise the speaker: absent on user turns and on rows written before
        // the summarizer stamped it (ADR-0054) — unknown, not defaulted.
        ...(m.agent ? { agents: [m.agent] } : {}),
      })),
    );
    setHydrated(true);
  }

  // Client-minted id for the workstream-to-be — stable across every pre-create
  // chat turn AND the eventual create, so the agent pipeline accumulates
  // discovery state (mode, summary, PRD artifact ledger) under ONE id instead
  // of minting a throwaway per turn (the bug: three turns, three ids, no state).
  const [pendingWorkstreamId] = useState(() => crypto.randomUUID());

  // Agent-proposed workstream identity (ADR-0044, additive-optional
  // `data.workstreamTitle`/`workstreamDescription` on the pre-create chat
  // response). First arrival triggers the auto-create below; held so a manual
  // "Create workstream" click after a failed auto-create still carries it.
  const [wsIdentity, setWsIdentity] = useState<{ title: string; description?: string } | null>(
    null,
  );

  // Post-creation composer draft + invite-agent selection — reset per workstream.
  const [conversationValue, setConversationValue] = useState('');
  const [invitedAgent, setInvitedAgent] = useState<string | null>(null);

  // Files staged for the next message — picked and validated, NOT yet uploaded.
  // Staging (rather than uploading on pick) is what makes the chip's X honest,
  // gives one code path either side of workstream creation, and lets the create
  // panel accept files at all: pre-create there is no workstream row for an
  // artifact to belong to (`resolveOwned` 404s), so the only thing that can
  // happen on pick is to hold the File.
  const [staged, setStaged] = useState<File[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  /** Re-entrancy guard for the send path — see {@link sendConversationTurn}. */
  const sendingRef = useRef(false);

  // Librarian routing mode, as last reported by the chat endpoint (additive-
  // optional `data.mode`). Server-owned; this is just the latest observation.
  const [wsMode, setWsMode] = useState<WorkstreamMode>('normal');

  // Consensus-tracker conflict detail for the latest turn (additive-optional
  // `data.consensus`/`data.conflictDetail`); null once a later turn agrees.
  const [conflict, setConflict] = useState<string | null>(null);

  // Agents that produced the latest reply, in execution order (additive-
  // optional `data.agents`) — rendered as the conversation status indicator.
  const [lastAgents, setLastAgents] = useState<string[]>([]);

  // The workstream's cross-turn artifact ledger (ADR-0041, additive-optional
  // `result.ledger`) as last reported — metadata only on the wire (url/preview
  // stripped server-side; the S3 key is the durable identity). It is the
  // registry that drives which files auto-open as tabs in the centre FileViewPanel
  // (hydrated on open, replaced by each turn's result.ledger), so a new
  // deliverable pops into view without the turn's result overwriting it.
  const [ledger, setLedger] = useState<TurnArtifact[]>([]);

  // Ledger hydration (the artifact analog of S05): reopening an existing
  // workstream mounts fresh with an empty ledger, so the in-chat deliverables
  // panel stayed blank until the next turn (never programmed to hydrate). Seed
  // it from the same durable `listArtifacts` read the Files rail uses (dedup'd
  // by query key — no double fetch). Guarded setState-during-render, seed-once
  // — after this the ledger is the live buffer each turn's `result.ledger`
  // replaces (frontend R2/R24, exactly as S05). The list carries the opaque
  // artifactId; rebuild the S3 key the panel re-presigns on (`artifactIdOf`
  // parses it back), url/preview unset — content is fetched on demand via
  // getArtifact (Drop-7 S02).
  const artifactsQuery = useArtifacts(initialWorkstreamId);
  // Live artifact count for the Files rail badge. Keyed on the CURRENT
  // workstream (not `initialWorkstreamId`) so the count appears the moment
  // auto-create mints one; same query key as FilesRailBody's, so React Query
  // dedupes it rather than issuing a second fetch.
  const filesQuery = useArtifacts(workstreamId);
  // Drop 8 / Phase G — the Files rail's "+ New" upload flow, keyed by the
  // active workstream. One hook instance shared by NewFileAction (the trigger)
  // and FilesRailBody (the progress/error display) so they observe the same
  // in-flight upload (frontend-architecture R1: single owner, lifted only as
  // far as the shared ancestor).
  // Also shared with the conversation composer's paperclip (Drop 9): every
  // attachment, from either entry point, goes through this one flow — so an
  // attached file lands in the Files rail instead of vanishing after the turn,
  // and only one upload is ever in flight.
  const upload = useUploadArtifact(workstreamId);

  /** Validate a multi-pick, stage what passes, report what didn't in one message. */
  const handleFilesPicked = useCallback((files: File[]) => {
    const rejections: string[] = [];
    const accepted = files.filter((file) => {
      const validated = validateUpload(file);
      if (!validated.ok) rejections.push(validated.message);
      return validated.ok;
    });
    setAttachError(rejections.length > 0 ? rejections.join(' ') : null);
    if (accepted.length === 0) return;
    // Same file picked twice (name + size + mtime) is a slip, not an intent —
    // staging it twice would upload two identical artifacts against the ledger cap.
    setStaged((prev) => [
      ...prev,
      ...accepted.filter(
        (f) =>
          !prev.some(
            (p) => p.name === f.name && p.size === f.size && p.lastModified === f.lastModified,
          ),
      ),
    ]);
  }, []);

  const removeStagedFile = useCallback((file: File) => {
    setStaged((prev) => prev.filter((f) => f !== file));
  }, []);

  /**
   * Upload the staged files into the workstream's ledger, one at a time, each
   * clearing its chip as it lands. `to` pins the destination for the create
   * flush, whose id has not reached state yet.
   *
   * Sequential, not parallel: `useUploadArtifact` tracks one filename +
   * progress, and finalize enforces the per-workstream artifact cap, so
   * concurrent uploads would race the counter and render an unreadable row.
   * A failure stops the queue and leaves the rest staged — the message is on
   * `upload.error` and sending again retries them, rather than dropping files
   * on the floor.
   */
  const flushStaged = useCallback(
    async (files: readonly File[], to?: string) => {
      for (const file of files) {
        try {
          await upload.uploadFileAsync(file, to);
        } catch {
          return;
        }
        setStaged((prev) => prev.filter((f) => f !== file));
      }
    },
    [upload],
  );

  const [ledgerHydrated, setLedgerHydrated] = useState(initialWorkstreamId === null);
  if (!ledgerHydrated && initialWorkstreamId !== null && artifactsQuery.data) {
    const wsId = initialWorkstreamId;
    setLedger(
      artifactsQuery.data.data.artifacts.map((a) => ({
        filename: a.filename,
        contentType: a.contentType,
        sizeBytes: a.sizeBytes,
        key: `workstreams/${wsId}/artifacts/${a.artifactId}/${a.filename}`,
        url: undefined,
        preview: undefined,
      })),
    );
    setLedgerHydrated(true);
  }

  // The seeded intro bubble's timestamp — stamped at mount (every fresh chat
  // is a fresh mount), so it reads "Just now" on open and ages with the
  // transcript.
  const [introCreatedAt] = useState(() => new Date().toISOString());

  // Coarse wall-clock tick (every 30s) fed into the render-time formatter so
  // "Just now" flips to a clock without per-second ticking. The sanctioned
  // external-system-sync effect (frontend-architecture R24/R25) — NOT
  // data-in-effect; one interval re-renders every bubble's time at once.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const sendMutation = useMutation({
    mutationKey: keys.workstream.createChat(),
    mutationFn: ({
      question,
      questionCreatedAt,
      attachment,
    }: {
      question: string;
      questionCreatedAt: string;
      /** Inline PDF for this turn only — derived from the staged files at send. */
      attachment?: Attachment;
    }) => {
      const apiMessages = transcript.map(({ role, content }) => ({ role, content }));
      return sendChatTurn(
        {
          messages: apiMessages,
          question,
          questionCreatedAt,
          workstreamId: pendingWorkstreamId,
          ...(attachment ? { attachment } : {}),
        },
        { getAccessToken },
      );
    },
    onSuccess: (data) => {
      // Render from the SERVER's authoritative timestamps (the echoed user turn
      // + the reply), not the local clock. Fall back defensively if an older
      // server omits them.
      setTranscript((prev) => [
        ...prev,
        {
          role: 'user',
          content: data.data.userMessage.content,
          createdAt: data.data.userMessage.createdAt ?? new Date().toISOString(),
        },
        {
          role: 'assistant',
          content: data.data.reply.content,
          createdAt: data.data.reply.createdAt ?? new Date().toISOString(),
          ...(data.data.reply.agent ? { agents: [data.data.reply.agent] } : {}),
        },
      ]);
      setComposerValue('');
      // Auto-create (ADR-0044): the first turn that arrives carrying an
      // agent-proposed title means the pipeline understood the ask — persist
      // the workstream now instead of waiting for a manual click. Messages are
      // passed explicitly (the setTranscript above hasn't flushed yet). A
      // failed auto-create surfaces createError in the modal and retries on
      // the next titled turn; the button stays as the manual fallback.
      const title = data.data.workstreamTitle;
      const description = data.data.workstreamDescription;
      if (title) setWsIdentity({ title, ...(description ? { description } : {}) });
      if (title && !workstreamId && !createMutation.isPending) {
        createMutation.mutate({
          messages: [
            ...transcript.map(({ role, content }) => ({ role, content })),
            { role: 'user', content: data.data.userMessage.content },
            { role: 'assistant', content: data.data.reply.content },
          ],
          title,
          ...(description ? { description } : {}),
        });
      }
    },
  });

  const createMutation = useMutation({
    mutationKey: keys.workstream.create(),
    // `void` (not an optional param) so the manual button's bare `mutate()`
    // stays legal; the auto-create path passes the fresh turn pair + identity
    // explicitly because its setTranscript hasn't flushed when it fires.
    mutationFn: (
      vars: {
        messages?: { role: 'user' | 'assistant'; content: string }[];
        title?: string;
        description?: string;
      } | void,
    ) => {
      const apiMessages =
        vars?.messages ?? transcript.map(({ role, content }) => ({ role, content }));
      const identity = vars?.title
        ? { title: vars.title, ...(vars.description ? { description: vars.description } : {}) }
        : wsIdentity
          ? {
              title: wsIdentity.title,
              ...(wsIdentity.description ? { description: wsIdentity.description } : {}),
            }
          : {};
      return createWorkstream(
        { messages: apiMessages, workstreamId: pendingWorkstreamId, ...identity },
        { getAccessToken },
      );
    },
    onSuccess: (data) => {
      // Nothing is appended to the transcript. Create is a persistence step, not
      // a conversational turn — the agent's genuine reply already landed on the
      // chat turn that triggered this create. The scripted "your workstream is
      // set up and ready" bubble that used to be appended here rendered directly
      // beneath that live discovery question, contradicting it.
      setWorkstreamId(data.data.workstreamId);
      // The workstream now exists, so the files staged in the create panel have
      // an owner and can finally upload (`resolveOwned` 404'd until this line).
      // The id is passed explicitly — `setWorkstreamId` above has not
      // re-rendered, so the upload hook still holds `null`.
      if (staged.length > 0) void flushStaged(staged, data.data.workstreamId);
      // Sync the URL (?ws=) so the rail highlights the new workstream. HomePage
      // pins the key while ?ws matches the session-created id, so no remount.
      onCreated(data.data.workstreamId);
      // S-C1: invalidate the rail's list so the new workstream appears without
      // a reload. void — invalidation is fire-and-forget; the next render of a
      // useWorkstreams() consumer triggers the refetch.
      void queryClient.invalidateQueries({ queryKey: keys.workstream.listMine() });
      // The conversation continues in the main pane, not the modal.
      setCreateOpen(false);
    },
  });

  // Post-creation chat turn — same client-held-transcript pattern as the
  // create flow, scoped to the now-real workstreamId + the implicit
  // 'default' focus (contract-correct; no focus persistence yet).
  const conversationMutation = useMutation({
    mutationKey: workstreamId ? keys.workstream.chat(workstreamId) : ['workstream', 'chat', 'none'],
    mutationFn: ({
      question,
      questionCreatedAt,
      action,
      attachment,
    }: {
      question: string;
      questionCreatedAt: string;
      action?: 'chat' | 'exitDiscovery';
      /** Inline PDF for this turn only — derived from the staged files at send. */
      attachment?: Attachment;
    }) => {
      if (!workstreamId) throw new Error('no active workstream');
      const apiMessages = transcript.map(({ role, content }) => ({ role, content }));
      // Async turn (ADR-0037): submit + poll inside one mutation — isPending
      // spans the whole turn, so the in-progress chrome needs no new state.
      return runWorkstreamChatTurn(
        {
          workstreamId,
          focusId: 'default',
          messages: apiMessages,
          question,
          questionCreatedAt,
          ...(invitedAgent ? { agentName: invitedAgent } : {}),
          ...(action ? { action } : {}),
          ...(attachment ? { attachment } : {}),
        },
        { getAccessToken },
      );
    },
    // Show the user's own message the instant they send (optimistic), not only
    // when the agent's reply arrives — it was previously appended in onSuccess,
    // so the chat looked frozen for the whole turn. The mutationFn runs
    // synchronously right after this (before React re-renders), reading the
    // pre-append transcript for the outgoing messages[], so this does NOT
    // double-post the question. Covers the exitDiscovery caller too.
    onMutate: ({ question, questionCreatedAt }) => {
      const optimisticTurn: TurnEntry = {
        role: 'user',
        content: question,
        createdAt: questionCreatedAt,
      };
      setTranscript((prev) => [...prev, optimisticTurn]);
      setConversationValue('');
      // Context for onError rollback (below).
      return { optimisticTurn, question };
    },
    onSuccess: ({ result }) => {
      setTranscript((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.reply.content,
          createdAt: result.reply.createdAt ?? new Date().toISOString(),
          // The turn result already names who handled it — the same list the
          // "Last reply: X → Y" status row reads. No extra round trip.
          ...(result.agents?.length ? { agents: result.agents } : {}),
        },
      ]);
      // Track the Librarian's routing mode when reported (gates the invite row
      // vs the exit-discovery affordance). Absent on older/stubbed servers.
      if (result.mode) setWsMode(result.mode);
      setLastAgents(result.agents ?? []);
      // Adopt the server's ledger when reported (absent leaves the last known
      // ledger standing — clearing it re-created the vanishing bug). Content
      // is fetched on view via getArtifact (Drop-7 S02) — no held cache.
      if (result.ledger) {
        setLedger(result.ledger);
        // Refresh the artifacts list ONLY when the ledger reports a deliverable
        // the list doesn't yet know — so it shows in the Files rail and joins
        // `everListedIds`. The derived open set already keeps a fresh ledger id
        // open meanwhile, so a re-reported unchanged ledger (ADR-0041 is
        // cumulative) needs no refetch.
        const listed = new Set(filesQuery.data?.data.artifacts.map((a) => a.artifactId));
        const hasNewFile = result.ledger.some((a) => {
          const id = artifactIdOf(a.key);
          return id !== null && !listed.has(id);
        });
        if (hasNewFile && workstreamId) {
          void queryClient.invalidateQueries({
            queryKey: keys.workstream.artifacts(workstreamId),
          });
        }
      }
      // Surface (or clear) the consensus conflict banner for this turn.
      setConflict(
        result.consensus === 'conflicted'
          ? (result.conflictDetail ?? 'The invited agents gave conflicting answers.')
          : null,
      );
      // (composer is cleared in onMutate, when the turn is sent)
      // Reconcile the persisted-transcript cache with the turn we just appended.
      // The chat is held in local `transcript`, but on remount — switching to
      // another workstream and back — the hydrate-once effect re-seeds it from
      // the `useMessages` cache. Without this invalidation that cache keeps its
      // open-time snapshot, so the remount re-hydrates a transcript MISSING the
      // turns added this session (they only reappear on a full refresh, which
      // refetches). R7: a write to the messages resource must reconcile its
      // query. `workstreamId` is non-null here (the mutationFn throws otherwise).
      if (workstreamId) {
        void queryClient.invalidateQueries({
          queryKey: keys.workstream.messages(workstreamId),
        });
      }
    },
    // Roll back the optimistic append when the turn fails: drop the dangling
    // user turn (else it lingers in `transcript` and replays as prior history
    // in the next turn's `messages[]`) and restore the composer text so the
    // user can retry without re-typing — but only if they haven't already
    // started a new message. The global MutationCache sink still raises the
    // error toast; this only undoes the optimistic state onMutate applied.
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      setTranscript((prev) => prev.filter((t) => t !== ctx.optimisticTurn));
      setConversationValue((cur) => (cur.length === 0 ? ctx.question : cur));
    },
  });

  /**
   * Send a conversation turn, flushing any staged files into the ledger first
   * so `read_artifact` can see them on this very turn.
   *
   * One staged PDF also rides inline: `read_artifact` returns text and cannot
   * extract PDF content, so the Bedrock Converse document block is an agent's
   * only way to read a PDF. The contract carries exactly one, within the invoke
   * payload cap — anything else is durable in Files and readable once PDF text
   * extraction lands (ADR-0041 §4).
   */
  const sendConversationTurn = useCallback(
    (question: string) => {
      // A turn now does async work (read the inline PDF, flush the staged files)
      // BEFORE the mutation starts, so `conversationMutation.isPending` — which
      // is what disables Send — is still false for that whole window. A second
      // Enter in it would upload every staged file twice and post two turns.
      // A ref, not state: the guard must take effect within the same tick.
      if (sendingRef.current) return;
      sendingRef.current = true;
      const files = staged;
      const pdf = inlinePdf(files);
      void (async () => {
        const attachment = pdf ? await readInlineAttachment(pdf) : null;
        if (files.length > 0) await flushStaged(files);
        conversationMutation.mutate({
          question,
          questionCreatedAt: new Date().toISOString(),
          ...(attachment ? { attachment } : {}),
        });
        sendingRef.current = false;
      })();
    },
    [staged, flushStaged, conversationMutation],
  );

  /**
   * Send a PRE-create turn, carrying any staged PDF inline. The files cannot be
   * uploaded yet (no workstream row until create — `resolveOwned` 404s), so
   * without the inline copy the discovery agent is asked about a file it was
   * never given, and answers that it has no artifacts. They still flush into the
   * ledger the moment auto-create mints the id.
   */
  const sendCreateTurn = useCallback(
    (question: string) => {
      // Reading the PDF happens BEFORE the mutation starts, so `isPending` —
      // what disables Send — is still false for that window. Same re-entrancy
      // guard as the post-create path, for the same reason.
      if (sendingRef.current) return;
      sendingRef.current = true;
      const pdf = inlinePdf(staged);
      void (async () => {
        const attachment = pdf ? await readInlineAttachment(pdf) : null;
        sendMutation.mutate({
          question,
          questionCreatedAt: new Date().toISOString(),
          ...(attachment ? { attachment } : {}),
        });
        sendingRef.current = false;
      })();
    },
    [staged, sendMutation],
  );

  // Map the client-held transcript → DS Message objects for the panel renderer.
  const dsMessages = transcript.map((m, i) => ({
    id: `turn-${i}`,
    // Identity per speaker, not one shared 'workstream-agent': the DS keys the
    // profile affordance off memberId, so collapsing six agents onto one id
    // makes every reply look like the same participant.
    memberId: m.role === 'user' ? 'user' : (m.agents?.[0] ?? 'workstream-agent'),
    // Name the speaker when the turn records one (ADR-0054), so a user can see
    // which agent said what instead of one undifferentiated "Workstream Agent".
    // A turn with no recorded agent keeps the generic name — never a blank, an
    // "undefined", or a guessed agent.
    memberName:
      m.role === 'user'
        ? (user?.name ?? 'You')
        : (m.agents?.map(agentLabel).join(' → ') ?? 'Workstream Agent'),
    // No memberRole: the `mira` variant already renders an "Agent" badge in the
    // meta line, so a role of "Agent" beside it is the same word twice.
    memberRole: '',
    memberType: m.role === 'user' ? ('human' as const) : ('agent' as const),
    // Ring tone by role family (agentVariant): coordination agents keep the
    // purple `mira` treatment that matches the seeded discovery intro;
    // specialists get the blue `agent` ring. An unattributed turn falls back to
    // `mira`, the same treatment it has always had.
    variant: m.role === 'user' ? ('human' as const) : agentVariant(m.agents),
    text: m.content,
    time: formatMessageTime(m.createdAt, now),
  }));

  const hasUserMessage = transcript.some((m) => m.role === 'user');
  // createDisabled: no user message yet, or create is in-flight, or already created.
  const createDisabled = !hasUserMessage || createMutation.isPending || workstreamId !== null;

  // Latch the typing indicator on for a beat even when the server answers fast,
  // so every send shows the "…is typing" animation instead of a dead flicker.
  const conversationPending = useMinVisible(conversationMutation.isPending);
  const sendPending = useMinVisible(sendMutation.isPending);

  const sendError = sendMutation.error ? errorMessage(sendMutation.error) : null;
  const createError = createMutation.error ? errorMessage(createMutation.error) : null;
  const conversationError = conversationMutation.error
    ? errorMessage(conversationMutation.error)
    : null;

  // The artifact panel renders beside the conversation only at the regular
  // tier — the DS prototype shell recipe (ArtifactWorkspace centre pane,
  // ChatPanel 40%/min-360 right). ponytail: compact tier keeps the full-pane
  // conversation and hides the panel; a compact artifact view is the R22
  // bottom-tab pattern, add when compact demand shows.
  const tier = useViewportTier();

  // Files open in the centre pane (S11): `?file=` is the active tab; `?files=`
  // orders + deep-links manually-Viewed uploads. The open SET is derived below
  // (ledger ∪ ?files= − closed − gone), reconciled against the artifacts list —
  // no self-heal round-trip through the viewer. Regular tier only (the compact
  // tier keeps a full-pane conversation; the R22 bottom-tab pattern is deferred).
  const [fileParams, setFileParams] = useSearchParams();
  // Tabs the user explicitly closed this session — kept OUT of the derived set
  // even though they stay in the ledger; a View re-opens by clearing the id.
  // Session state (resets on remount) so a closed tab never springs back.
  const [closedFileIds, setClosedFileIds] = useState<ReadonlySet<string>>(() => new Set());

  // The artifacts list (`useArtifacts`) is the AUTHORITATIVE set of files that
  // exist; the ledger only covers the brief window where a just-produced
  // deliverable isn't listed yet. Track every id ever listed, so we can tell a
  // file that WAS listed and is now absent (deleted here or in another session /
  // evicted) from one that simply hasn't listed yet.
  const listedIds = workstreamId
    ? filesQuery.data?.data.artifacts.map((a) => a.artifactId)
    : undefined;
  // Accumulate every id ever listed (to tell a deleted / evicted file — was listed,
  // now gone — from a fresh deliverable not listed yet). Guarded setState DURING
  // render — the repo's sanctioned "adjust state when data arrives" pattern (like
  // the ledger hydration above), NOT an effect (react-hooks/set-state-in-effect).
  const [everListedIds, setEverListedIds] = useState<ReadonlySet<string>>(() => new Set());
  if (listedIds && listedIds.some((id) => !everListedIds.has(id))) {
    const next = new Set(everListedIds);
    listedIds.forEach((id) => next.add(id));
    setEverListedIds(next);
  }

  // The open tab set is DERIVED, not a second URL writer (an effect that wrote
  // `?files=` raced the rail's `?ws=` navigation). It is the union of the ledger
  // files (auto-opened on open + as new deliverables arrive) and any manually-
  // Viewed files (`?files=`, kept so a Viewed upload orders + deep-links), minus
  // the ones the user closed AND the ones reconciled GONE. `?file=` is the active
  // tab.
  const existingIds = listedIds ? new Set(listedIds) : null;
  const ledgerFileIds = workstreamId
    ? ledger.map((a) => artifactIdOf(a.key)).filter((id): id is string => id !== null)
    : [];
  const manualFileIds = workstreamId ? parseFileTabs(fileParams.get(FILES_PARAM)) : [];
  // Reconcile against the authoritative list: drop closed + gone ids (pure +
  // exhaustively unit-tested in lib/openFiles). Gone is DERIVED (not a permanent
  // close), so a transient list blip recovers while a real delete stays out.
  const openFileIds = deriveOpenFileIds({
    manualIds: manualFileIds,
    ledgerIds: ledgerFileIds,
    closedIds: closedFileIds,
    existingIds,
    everListedIds,
  });
  // Filenames for the open tabs, from the ledger — so a just-produced deliverable
  // is labelled immediately (before the artifacts list refetches to include it);
  // FileViewPanel falls back to the artifacts list for manually-Viewed uploads.
  const fileLabels = new Map(
    ledger
      .map((a) => [artifactIdOf(a.key), a.filename] as const)
      .filter((pair): pair is readonly [string, string] => pair[0] !== null),
  );
  const rawActiveFile = workstreamId ? fileParams.get(FILE_PARAM) : null;
  // The active tab must be one of the open tabs; fall back to the first open one.
  const activeFileId =
    rawActiveFile && openFileIds.includes(rawActiveFile) ? rawActiveFile : (openFileIds[0] ?? null);
  const showFileView = openFileIds.length > 0 && tier === 'regular';

  // View: (re)open + activate a file. Un-closes it and records it in `?files=`
  // (so a manually-Viewed upload — one not in the ledger — stays open + deep-links).
  const viewFile = useCallback(
    (artifactId: string) => {
      setClosedFileIds((prev) => {
        if (!prev.has(artifactId)) return prev;
        const next = new Set(prev);
        next.delete(artifactId);
        return next;
      });
      setFileParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const open = parseFileTabs(next.get(FILES_PARAM));
          if (!open.includes(artifactId)) open.push(artifactId);
          next.set(FILES_PARAM, open.join(','));
          next.set(FILE_PARAM, artifactId);
          return next;
        },
        { replace: true },
      );
    },
    [setFileParams],
  );
  // Activate an already-open tab.
  const selectFile = useCallback(
    (artifactId: string) =>
      setFileParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(FILE_PARAM, artifactId);
          return next;
        },
        { replace: true },
      ),
    [setFileParams],
  );
  // Close a tab: remember it as closed (so the ledger won't re-open it) and drop
  // it from the manual list + active pointer. The derived set re-picks the active
  // tab; a re-View clears the closed flag.
  const closeFile = useCallback(
    (artifactId: string) => {
      const idx = openFileIds.indexOf(artifactId);
      if (idx === -1) return; // not open — nothing to close (no churn, no stale poison)
      // When closing the ACTIVE tab, re-activate the neighbour (not the first tab),
      // using the derived open order so an auto-opened tab reselects correctly too.
      const remaining = openFileIds.filter((id) => id !== artifactId);
      const nextActive =
        activeFileId === artifactId
          ? (remaining[Math.min(idx, remaining.length - 1)] ?? null)
          : null;
      setClosedFileIds((prev) => {
        const next = new Set(prev);
        next.add(artifactId);
        return next;
      });
      setFileParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const rem = parseFileTabs(next.get(FILES_PARAM)).filter((id) => id !== artifactId);
          if (rem.length > 0) next.set(FILES_PARAM, rem.join(','));
          else next.delete(FILES_PARAM);
          if (nextActive) next.set(FILE_PARAM, nextActive);
          else if (next.get(FILE_PARAM) === artifactId) next.delete(FILE_PARAM);
          return next;
        },
        { replace: true },
      );
    },
    [openFileIds, activeFileId, setFileParams],
  );
  // NOTE: a deleted / evicted file needs no explicit close — once it leaves the
  // artifacts list (`useDeleteArtifact` invalidates it), `deriveOpenFileIds`
  // reconciles it "gone" and its tab self-closes. No `onPrune` / delete callback
  // + no permanent close-poisoning; a transient blip recovers.

  // Derived, not stored (R2) — looked up from the already-cached rail list;
  // falls back before the S-C1 invalidation's refetch lands post-create.
  const activeTitle =
    workstreamsQuery.data?.data.find((w) => w.workstreamId === workstreamId)?.title ?? 'Workstream';

  // firstName from the JWT `name` claim. No useMemo — the React Compiler
  // memoizes (R11/R2). trim() before split so a leading space can't yield an
  // empty first token (mirrors the DS Avatar's own initials derivation).
  const firstName = user?.name?.trim().split(/\s+/)[0] ?? 'there';
  const fullName = user?.name ?? 'User';

  // Brand tint is DATA from tenantBrand (the bgprivate map → '#1e40af'), not a
  // hardcoded style token. Undefined-safe for any not-yet-mapped tenant.
  const tint = tenantBrand?.avatarTint;
  const brandName = tenantBrand?.displayName;

  const trailing = (
    <>
      <IconButton
        icon={<Icon as={icons.Bell} />}
        aria-label="Notifications"
        size="sm"
        variant="ghost"
      />
      {/* The working profile menu / Sign Out (S-H2) — replaces the bare
          trailing avatar. The EmptyState's display avatar below is
          unchanged. UserMenu reads its own claims via useAuth(). */}
      <UserMenu />
    </>
  );

  return (
    <div
      style={{
        // height (not minHeight): a definite viewport height so the RailPanel's
        // height:100% chain resolves and its content scrolls internally instead
        // of the whole page scrolling. overflow:hidden clips the shell to the
        // viewport; each inner pane owns its own scroll.
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: theme.color.neutral.bgSurface,
      }}
    >
      <TopNav trailing={trailing} />
      <WorkspaceShell
        workstreamsPanel={<WorkstreamsRailBody onNew={onNew} />}
        workstreamsAction={<NewWorkstreamAction onClick={onNew} />}
        selectedWorkstreamId={workstreamId}
        filesCount={workstreamId ? (filesQuery.data?.data.artifacts.length ?? null) : null}
        filesPanel={
          workstreamId ? (
            <FilesRailBody
              workstreamId={workstreamId}
              onView={viewFile}
              uploading={
                upload.isUploading
                  ? { filename: upload.filename ?? '', progress: upload.progress }
                  : null
              }
              uploadError={upload.error}
            />
          ) : undefined
        }
        filesAction={
          workstreamId ? (
            <NewFileAction onSelectFile={upload.uploadFile} disabled={upload.isUploading} />
          ) : undefined
        }
      >
        {workstreamId ? (
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {showFileView && activeFileId ? (
              <FileViewPanel
                workstreamId={workstreamId}
                openFileIds={openFileIds}
                activeFileId={activeFileId}
                labels={fileLabels}
                onSelect={selectFile}
                onClose={closeFile}
              />
            ) : null}
            <WorkspaceConversation
              title={activeTitle}
              fluid={!showFileView}
              messages={dsMessages}
              value={conversationValue}
              onChange={setConversationValue}
              onSend={sendConversationTurn}
              sendDisabled={
                conversationMutation.isPending ||
                upload.isUploading ||
                conversationValue.length > MAX_TEXT_LEN ||
                undefined
              }
              maxLength={MAX_TEXT_LEN}
              pending={conversationPending}
              sendError={conversationError}
              invitedAgent={invitedAgent}
              onInviteAgent={setInvitedAgent}
              mode={wsMode}
              activeAgents={lastAgents}
              conflict={conflict}
              stagedFiles={staged}
              onFilesPicked={handleFilesPicked}
              onRemoveFile={removeStagedFile}
              uploadingFilename={upload.isUploading ? upload.filename : null}
              uploadProgress={upload.progress}
              attachError={attachError ?? upload.error}
              onExitDiscovery={() =>
                conversationMutation.mutate({
                  question: 'exit discovery',
                  questionCreatedAt: new Date().toISOString(),
                  action: 'exitDiscovery',
                })
              }
            />
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: theme.space[6],
            }}
          >
            <EmptyState
              icon={
                <Avatar
                  name={fullName}
                  size="display"
                  style={tint ? { background: tint, boxShadow: `0 0 0 2px ${tint}` } : undefined}
                />
              }
              title={
                <>
                  {brandName && (
                    <span
                      style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.caption,
                        fontWeight: theme.typography.fontWeight.semibold,
                        letterSpacing: theme.typography.letterSpacing.label,
                        textTransform: 'uppercase',
                        color: tint,
                        marginBottom: theme.space['1_5'],
                      }}
                    >
                      {brandName}
                    </span>
                  )}
                  Welcome to Genesis, {firstName}.
                </>
              }
              description={
                hasWorkstreams
                  ? `You're set up for ${
                      brandName ?? 'Genesis'
                    }. Select a workstream from the left to pick up where you left off.`
                  : `You're set up for ${
                      brandName ?? 'Genesis'
                    }. When you create your first workstream, it'll appear here. Or one of your colleagues will share theirs with you.`
              }
              action={
                // Show the "Create First Workstream" CTA only when we KNOW the
                // list is empty — hide it while loading (avoid a CTA flash) and
                // once any workstream exists (the rail + `+ New` action are the
                // create affordance from then on).
                workstreamsQuery.isSuccess && !hasWorkstreams ? (
                  // Matches the StratBridge_Daniel story's `EmptyStateCta` exactly:
                  // ghostStrong + 10x24 padding, body-coloured text, fainter border,
                  // 13px / 600 (bodySm / semibold) — not the default Button metrics.
                  <Button
                    variant="ghostStrong"
                    style={{
                      padding: '10px 24px',
                      color: theme.color.neutral.body,
                      borderColor: theme.color.neutral.fainter,
                      fontSize: theme.typography.fontSize.bodySm,
                      fontWeight: theme.typography.fontWeight.semibold,
                    }}
                    onClick={() => setCreateOpen(true)}
                  >
                    + Create First Workstream
                  </Button>
                ) : undefined
              }
            />
          </div>
        )}
      </WorkspaceShell>
      <CreateWorkstreamPanel
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        messages={dsMessages}
        introTime={formatMessageTime(introCreatedAt, now)}
        value={composerValue}
        onChange={setComposerValue}
        onSend={sendCreateTurn}
        // Block send while a turn is in flight OR the draft exceeds the shared
        // contract limit (MAX_TEXT_LEN) — the panel shows inline feedback so the
        // user knows BEFORE clicking, instead of a post-send server rejection.
        // `|| undefined` so the Composer's own empty-draft guard still applies.
        sendDisabled={sendMutation.isPending || composerValue.length > MAX_TEXT_LEN || undefined}
        maxLength={MAX_TEXT_LEN}
        pending={sendPending}
        onCreate={() => createMutation.mutate()}
        createDisabled={createDisabled}
        creating={createMutation.isPending}
        sendError={sendError}
        createError={createError}
        stagedFiles={staged}
        onFilesPicked={handleFilesPicked}
        onRemoveFile={removeStagedFile}
        uploadingFilename={upload.isUploading ? upload.filename : null}
        uploadProgress={upload.progress}
        attachError={attachError ?? upload.error}
      />
    </div>
  );
}
WorkstreamSession.displayName = 'WorkstreamSession';

/**
 * HomePage — the protected '/' home (behind RequireAuth + RequireTenant).
 *
 * Owns WHICH chat is active; all per-chat state lives in WorkstreamSession.
 * The active workstream is URL state (`?ws=`, R3 — the rail writes it); a
 * draft (not-yet-created) chat is keyed by a client-minted id. Changing the
 * key remounts the session — that IS the reset (and the switch): opening an
 * existing chat hydrates its transcript from `GET /messages` (Drop 7 / S05,
 * ADR-0047); a draft with no persisted messages opens empty.
 */
export function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeWsId = searchParams.get(WS_PARAM);
  // "+ New" mints a fresh draft id → key change → full session reset.
  const [draft, setDraft] = useState(() => ({ id: crypto.randomUUID(), openCreate: false }));
  // The workstream the CURRENT draft session created. While ?ws points at it,
  // keep the draft key — remounting would wipe the live transcript.
  const [createdId, setCreatedId] = useState<string | null>(null);
  // Navigating to a DIFFERENT workstream ends the draft session — drop the pin
  // (render-time state adjustment, guarded so it fires once), else clicking
  // back to the created workstream would resurrect the draft (and its modal)
  // instead of opening the workstream. Draft transcripts die on switch until
  // hydration lands (concept plan Stage 3f).
  if (createdId !== null && activeWsId !== null && activeWsId !== createdId) {
    setCreatedId(null);
  }
  const isOwnDraft = activeWsId === null || activeWsId === createdId;

  return (
    <WorkstreamSession
      key={isOwnDraft ? draft.id : activeWsId}
      initialWorkstreamId={isOwnDraft ? null : activeWsId}
      autoOpenCreate={isOwnDraft && draft.openCreate}
      onNew={() => {
        setDraft({ id: crypto.randomUUID(), openCreate: true });
        setCreatedId(null);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(WS_PARAM);
            next.delete(FILES_PARAM);
            next.delete(FILE_PARAM);
            return next;
          },
          { replace: true },
        );
      }}
      onCreated={(id) => {
        setCreatedId(id);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(WS_PARAM, id);
            return next;
          },
          { replace: true },
        );
      }}
    />
  );
}
HomePage.displayName = 'HomePage';
